import React from "react";
import Link from "next/link";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/cards/card/card";
import { ContentCard, Blocks } from "@/types/cms";
import { renderRichTextWithMutedStrikethrough, getFirstHeadingFromBlocks } from "@/lib/richText";
import { resolveLucideIcon } from "@/lib/lucideIcon";
import ResponsiveImage, { type ImageSource } from "@/components/utilities/ResponsiveImage";
import { optimizeVideoUrlAutoGood, getCloudinaryVideoPosterUrl } from "@/lib/imageOptimization";
import { optimizeCloudinaryUrl } from "@/lib/videoConfig";
import { DEFAULT_CARD_IMAGE_SIZES } from "@/lib/cardImageSizes";
import VideoPlayer from "@/components/video/VideoPlayer";
import CardPrice from "@/components/cards/cardPrice/cardPrice";
import TestimonialCard from "@/components/cards/testimonialCard/TestimonialCard";
import CardLesson from "@/components/cards/cardLesson/cardLesson";
import CardOverlay from "@/components/cards/cardOverlay/cardOverlay";
import { Button } from "@/components/button/button/button";
import {
  normalizeCardButtons,
  splitButtons,
  type ButtonVisibility,
} from "@/components/cards/cardOverlay/buttonVisibility";
import { cn } from "@/lib/utils";
import { highlightFeatureDne } from "@/lib/featureDne";
import { toMediaNode, type CmsMediaNode } from "@/lib/cmsHelpers";
import styles from "./cardContent.module.css";

/** A rich-text inline node (leaf or link). */
type RichTextChild = {
  text?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  type?: string;
  url?: string;
  children?: RichTextChild[];
};

/** A rich-text block (heading/paragraph/list/...). */
type RichTextBlock = {
  type?: string;
  level?: number;
  textAlign?: "left" | "center" | "right" | "justify";
  children?: RichTextChild[];
};

/** Extra media fields the CMS attaches to cards that aren't on the lightweight
 *  ContentCard type (responsive image variants + an optional video). The raw
 *  values come in several CMS shapes, so they're unwrapped via `toMediaNode`
 *  before use. */
type CardWithMedia = ContentCard & {
  imageDesktop?: unknown;
  imageTablet?: unknown;
  video?: unknown;
};

/** Renders a dynamically-resolved Lucide icon. Hoisted to module scope so the
 *  per-card resolved component is passed as data rather than created during
 *  render (satisfies react-hooks/static-components). */
function DynamicIcon({
  icon: Icon,
  className,
  strokeWidth,
}: {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  className?: string;
  strokeWidth?: number;
}) {
  return <Icon className={className} strokeWidth={strokeWidth} />;
}

interface CardContentItemProps {
  card: ContentCard;
  icon?: string;
  title?: string;
  subtitle?: string;
  body?: Blocks | string;
  shadow?: boolean;
  variant?: "default" | "testimonial" | "price" | "lesson";
  /**
   * Next <Image> `sizes` for the card's media. Reflects the card's rendered
   * width in its layout so the browser fetches a right-sized file instead of
   * the deviceSizes-max (see `@/lib/cardImageSizes`). Defaults to the 3-up grid
   * width; callers in wider layouts (hero, 1-up, split columns) pass their own.
   */
  imageSizes?: string;
  /**
   * Eager-load + high fetch priority for the media — set on the hero (LCP)
   * image so it isn't lazy-loaded behind the fold. Off for everything else.
   */
  priority?: boolean;
  /**
   * Encode quality for the media. Defaults to Next's 75; the hero passes a
   * higher value (`HERO_IMAGE_QUALITY`) to offset double compression at large
   * sizes. Must be listed in `images.qualities` (next.config.ts).
   */
  imageQuality?: number;
}

/** Whether a card has any CMS image (desktop/tablet/mobile). Mirrors the image
 *  resolution in the default render below, so the "lesson" variant only falls
 *  back to the code panel when no image is set — a CMS image still wins. */
function lessonCardHasImage(card: CardWithMedia): boolean {
  const desktop =
    toMediaNode(card?.imageDesktop) || toMediaNode(card?.image);
  const tablet = toMediaNode(card?.imageTablet);
  const mobile = toMediaNode(card?.imageMobile);
  return Boolean(desktop?.url || tablet?.url || mobile?.url);
}

const CardContentItem = ({ card, icon, title, subtitle, body, shadow = false, variant = "default", imageSizes = DEFAULT_CARD_IMAGE_SIZES, priority = false, imageQuality }: CardContentItemProps) => {
  const blocksFromCard = Array.isArray(card.content)
    ? card.content
    : Array.isArray(card.body)
      ? card.body
      : undefined;
  const overrideBlocks = Array.isArray(body) ? (body as Blocks) : undefined;
  const blocks = overrideBlocks || blocksFromCard;

  const headerFromBlocks = blocks ? getFirstHeadingFromBlocks(blocks) : undefined;
  const headerText = title ?? headerFromBlocks ?? card.header ?? "";
  const headingBlock = (blocks as RichTextBlock[] | undefined)?.find(
    (b) => b.type === 'heading',
  ) as RichTextBlock | undefined;
  const headingLevel = headingBlock?.level ?? 2;

  // Check if heading contains any italic text
  const hasItalicText = headingBlock?.children?.some((child: RichTextChild) => child.italic) ?? false;
  
  const headingClass =
    headingLevel === 1
      ? "title-2xl"
      : headingLevel === 2
      ? "title-xl"
      : headingLevel === 3
      ? "title-lg"
      : headingLevel === 4
      ? "title-md"
      : headingLevel === 5
      ? "title-sm"
      : "title-xs";

  const accentClassForHeading =
    headingLevel === 1
      ? "accent-2xl"
      : headingLevel === 2
      ? "accent-xl"
      : headingLevel === 3
      ? "accent-lg"
      : headingLevel === 4
      ? "accent-md"
      : headingLevel === 5
      ? "accent-sm"
      : "accent-xs";
  
  // If heading has italic text, use accent class for line-height
  const finalHeadingClass = hasItalicText ? accentClassForHeading : headingClass;

  const headingInline = headingBlock ? (
    <span>
      {headingBlock.children?.map((child: RichTextChild, idx: number) => {
        let content: React.ReactNode = child.text;
        if (child.bold) content = <strong>{content}</strong>;
        if (child.italic) {
          content = <span className={accentClassForHeading}>{content}</span>;
        }
        if (child.underline) content = <u>{content}</u>;
        if (child.type === 'link' && child.url) {
          const linkText = (Array.isArray(child.children) ? child.children.map((c: RichTextChild) => c.text).join('') : child.text) || '';
          content = (
            <a href={child.url} target="_blank" rel="noopener noreferrer" style={{ whiteSpace: 'pre-wrap' }}>
              {linkText}
            </a>
          );
        }
        const spanClass = child.strikethrough ? 'muted' : '';
        return (
          <span key={idx} className={spanClass} style={{ whiteSpace: 'pre-wrap' }}>
            {content}
          </span>
        );
      })}
    </span>
  ) : null;

  // Remove the first heading block from body rendering
  const bodyBlocks = blocks
    ? (blocks as RichTextBlock[]).filter(
        (block, index) =>
          block.type !== 'heading' ||
          index !== (blocks as RichTextBlock[]).findIndex((b) => b.type === 'heading'),
      )
    : undefined;

  // Price variant: use dedicated CardPrice component.
  // If the card's custom ID contains both "price" and "free", render the
  // free-price layout (first body paragraph moves above the CTA).
  if (variant === "price") {
    const ccid = String(card?.contentCard_customID ?? "")
      .toLowerCase()
      .trim();
    const freeVariant = ccid.includes("price") && ccid.includes("free");
    return <CardPrice card={card} freeVariant={freeVariant} />;
  }

  // Testimonial variant: delegate to dedicated TestimonialCard component
  if (variant === "testimonial") {
    return <TestimonialCard card={card} body={body} />;
  }

  // Lesson variant: render the code "Your Lesson Plan" panel ONLY when the card
  // has no CMS image — a configured image still wins (falls through to the
  // default image render below). Sticky behavior is unchanged either way (same
  // grid column).
  if (variant === "lesson" && !lessonCardHasImage(card)) {
    return <CardLesson card={card} />;
  }

  // Default variant rendering
  const IconComponent = resolveLucideIcon(icon ?? card.icon ?? undefined);

  // Card-level alignment is no longer a separate field — alignment comes
  // from each block's own `textAlign` (set by the rich text editor's
  // alignment toolbar and surfaced via lexicalToBlocks). The title
  // inherits the first heading block's alignment; body paragraphs each
  // carry their own.
  const titleAlign = headingBlock?.textAlign;
  const alignmentClass =
    titleAlign === "center"
      ? styles.alignCenter
      : titleAlign === "right"
        ? styles.alignRight
        : titleAlign === "justify"
          ? styles.alignJustify
          : styles.alignLeft;

  // Resolve buttonVisibility → which buttons render where.
  const allCardButtons = normalizeCardButtons(card?.buttons);
  const { cardButtons, overlayButtons } = splitButtons(
    allCardButtons,
    card?.buttonVisibility as ButtonVisibility | undefined
  );

  // Single-CTA shortcut: when the card has exactly one button (regardless
  // of visibility — including "hidden"), the whole card surface becomes
  // that button's link. With multiple buttons we leave the card alone so
  // editors can pick between specific CTAs. A button without an href can't
  // act as the wrapper, so we fall back to default behavior in that case.
  const wholeCardCta =
    allCardButtons.length === 1 && allCardButtons[0].href
      ? allCardButtons[0]
      : null;

  // CMS-driven surface toggles. Border or background → 16px content padding.
  const hasBorder = card?.border === true;
  const hasBackground = card?.background === true;
  const hasCornerRadius = card?.cornerRadius === true;
  const needsPadding = hasBorder || hasBackground;

  const iconPlacement = (card?.iconPlacement ?? 'above') as
    | 'above'
    | 'left'
    | 'left-vcenter'
    | 'left-hug'
    | 'right';
  const iconOnEyebrow =
    card?.iconOnEyebrow === true && !!card?.eyebrow;
  const isSideIcon =
    !!IconComponent &&
    !iconOnEyebrow &&
    (iconPlacement === 'left' ||
      iconPlacement === 'left-vcenter' ||
      iconPlacement === 'right');
  // "Hug title": the icon sits inline next to the title only (not a side column),
  // so the subheader/body flow full-width below.
  const hugIcon = !!IconComponent && !iconOnEyebrow && iconPlacement === 'left-hug';

  const headerEl = (
      <CardHeader className={cn(styles.header, alignmentClass)}>

        {/* Icon above the title (default placement) */}
        {IconComponent && !iconOnEyebrow && iconPlacement === 'above' && (
          <div className={styles.iconWrapper}>
            <DynamicIcon icon={IconComponent} className={styles.icon} strokeWidth={1.5} />
          </div>
        )}
        
        {/* Video support - check for video field first */}
        {(() => {
          const media = card as CardWithMedia;
          const videoData = toMediaNode(media.video);
          const videoUrl = videoData?.url;

          if (videoUrl) {
            const optimizedVideoUrl = optimizeVideoUrlAutoGood(videoUrl) ?? videoUrl;
            const posterUrl = getCloudinaryVideoPosterUrl(videoUrl);

            return (
              <VideoPlayer
                src={optimizedVideoUrl}
                poster={posterUrl}
                videoClassName={cn(styles.image, shadow && "shadow-card")}
              />
            );
          }

          // Fall back to image if no video
          const desktop =
            toMediaNode(media.imageDesktop) || toMediaNode(media.image);
          const tablet = toMediaNode(media.imageTablet);
          const mobile = toMediaNode(media.imageMobile);

          const desktopUrl = desktop?.url;
          const tabletUrl = tablet?.url;
          const mobileUrl = mobile?.url;
          const anyUrl = desktopUrl || tabletUrl || mobileUrl || "";
          if (!anyUrl) return null;

          // Build ImageSource objects so <Image> gets the *real* Cloudinary
          // dimensions — otherwise CSS `height: auto` derives height from a
          // fallback ratio and the image gets squashed into 3:2.
          const toImageSource = (
            node: CmsMediaNode | null,
            url: string | undefined
          ): ImageSource | null => {
            if (!url) return null;
            const width = Number(node?.width) || 0;
            const height = Number(node?.height) || 0;
            const src = optimizeCloudinaryUrl(url, false);
            if (!width || !height) {
              // Payload almost always ships width/height on media uploads.
              // If it's missing for some reason, 1200×800 is the historical
              // default — better than crashing <Image>.
              return { src, width: 1200, height: 800 };
            }
            return { src, width, height };
          };

          const desktopSource = toImageSource(desktop, desktopUrl);
          const tabletSource = toImageSource(tablet, tabletUrl);
          const mobileSource = toImageSource(mobile, mobileUrl);

          // If the media URL is actually a video (e.g. Cloudinary video attached to image field),
          // render it with the VideoPlayer instead of as an image.
          const isVideoFromImageField = /\.(mp4|mov|webm|avi|m4v)$/i.test(anyUrl);
          if (isVideoFromImageField) {
            const optimizedVideoUrl = optimizeVideoUrlAutoGood(anyUrl) ?? anyUrl;
            const posterUrl = getCloudinaryVideoPosterUrl(anyUrl);

            return (
              <VideoPlayer
                src={optimizedVideoUrl}
                poster={posterUrl}
                videoClassName={cn(styles.image, shadow && "shadow-card")}
              />
            );
          }

          return (
            <ResponsiveImage
              desktop={desktopSource}
              tablet={tabletSource}
              mobile={mobileSource}
              alt={headerText || "Card image"}
              className={cn(styles.image, shadow && "shadow-card")}
              sizes={imageSizes}
              priority={priority}
              quality={imageQuality}
            />
          );
        })()}

        {card.eyebrow && (
          <div className={styles.eyebrowRow}>
            {IconComponent && iconOnEyebrow && (
              <DynamicIcon icon={IconComponent} className={styles.eyebrowIcon} strokeWidth={1.5} />
            )}
            <span className={cn("eyebrow", styles.eyebrow)}>{card.eyebrow}</span>
          </div>
        )}
        {hugIcon ? (
          <div
            className={cn(
              styles.titleRow,
              titleAlign === 'center' && styles.titleRowCenter,
              titleAlign === 'right' && styles.titleRowEnd,
            )}
          >
            <DynamicIcon icon={IconComponent} className={styles.icon} strokeWidth={1.5} />
            <CardTitle className={finalHeadingClass}>
              {headingInline ?? highlightFeatureDne(headerText)}
            </CardTitle>
          </div>
        ) : (
          <CardTitle className={finalHeadingClass}>
            {headingInline ?? highlightFeatureDne(headerText)}
          </CardTitle>
        )}
        {(subtitle ?? card.subheader) && (
          <CardDescription className="title-sm">
            {subtitle ?? card.subheader}
          </CardDescription>
        )}
      </CardHeader>
  );

  const contentEl = (
      <CardContent>
        <CardDescription className={cn("body-copy", styles.body)}>
          {bodyBlocks
            ? renderRichTextWithMutedStrikethrough(bodyBlocks, {
                // The renderer's default is `body-md` (16px, fixed). Card
                // body prose uses the shared running-copy size instead, so
                // it matches section intros and the hero subtitle.
                paragraphClass: "body-copy",
              })
            : typeof body === 'string'
              ? body
              : typeof card.body === 'string'
                ? card.body
                : null}
        </CardDescription>

        {cardButtons.length > 0 && (
          <div className={styles.buttons}>
            {cardButtons.map((btn, idx) => {
              const label = btn.copy || btn.title || "";
              // When the card opens an overlay (overlayButtons.length > 0)
              // these inline buttons are visual only — clicking the card
              // surface is what triggers navigation/overlay. Otherwise the
              // first button with a URL navigates.
              // Inline button is visual-only when the whole card is the
              // link (avoids invalid nested <a>) or when the card surface
              // opens an overlay.
              const renderAsLink =
                !wholeCardCta && overlayButtons.length === 0 && !!btn.href;
              const target = btn.isBlank ? "_blank" : undefined;
              const rel = btn.isBlank ? "noopener noreferrer" : undefined;
              return (
                <Button
                  key={btn.id ?? idx}
                  variant={btn.variant}
                  size={btn.size}
                  asChild
                  className={styles.cardButton}
                >
                  {renderAsLink ? (
                    <Link
                      href={btn.href}
                      target={target}
                      rel={rel}
                      title={btn.title}
                    >
                      {highlightFeatureDne(label)}
                    </Link>
                  ) : (
                    <span title={btn.title}>{highlightFeatureDne(label)}</span>
                  )}
                </Button>
              );
            })}
          </div>
        )}
      </CardContent>
  );

  const cardInner = (
    <Card
      className={cn(
        styles.root,
        hasBorder && styles.bordered,
        hasBackground && styles.filled,
        hasCornerRadius && styles.rounded,
        needsPadding && styles.padded,
      )}
    >
      {isSideIcon ? (
        <div
          className={cn(
            styles.iconRow,
            iconPlacement === 'right' && styles.iconRowReverse,
            iconPlacement === 'left-vcenter' && styles.iconRowVCenter,
            titleAlign === 'center' && styles.iconRowCenter,
            titleAlign === 'right' && styles.iconRowEnd,
          )}
        >
          <div className={styles.iconAside}>
            {IconComponent && <DynamicIcon icon={IconComponent} className={styles.icon} strokeWidth={1.5} />}
          </div>
          <div className={styles.iconRowText}>
            {headerEl}
            {contentEl}
          </div>
        </div>
      ) : (
        <>
          {headerEl}
          {contentEl}
        </>
      )}
    </Card>
  );

  if (wholeCardCta) {
    return (
      <Link
        href={wholeCardCta.href}
        target={wholeCardCta.isBlank ? "_blank" : undefined}
        rel={wholeCardCta.isBlank ? "noopener noreferrer" : undefined}
        title={wholeCardCta.title}
        aria-label={headerText || wholeCardCta.copy || wholeCardCta.title}
        className={cn(
          styles.clickable,
          card?.underlineOnHover === true && styles.underlineOnHover,
        )}
      >
        {cardInner}
      </Link>
    );
  }

  if (overlayButtons.length > 0) {
    return (
      <CardOverlay
        buttons={overlayButtons}
        title={headerText || undefined}
        trigger={
          <div
            role="button"
            tabIndex={0}
            className={styles.clickable}
            aria-label={headerText ? `Open ${headerText} options` : "Open options"}
          >
            {cardInner}
          </div>
        }
      />
    );
  }

  return cardInner;
};

export default CardContentItem;
